import type { ReactElement } from 'react';
import { PageShell } from '../Page';
import type { PageProps } from '../types';
import { EditableText } from '../../components/Editable';
import { ImageSlot } from '../../components/ImageSlot';
import { useBindings } from '../../state/BindingsContext';
import type { SelectionField } from '../../state/useSelectionContext';
import { audienceImageQuery, patchAudienceSegment, tacticAudienceCards } from '../../lib/audiencePresentation';
import {
  channel,
  deliveryKind,
  pricedLine,
  productLabel,
  tacticGroupDisplayTitle,
  tacticDeliveryMetrics,
  tacticFor,
  tacticGroupMetricLine,
  defaultTacticRole,
} from '../../lib/plan';
import { useContentLibrary } from '../../state/ContentLibraryContext';
import { Frame, HeadRow, NumberedLabel, SubCard, TagPill } from './primitives';

/**
 * tactic_audience (design.md V12): CONTENT+PANEL light. Binds exactly like
 * momentum_v1's `TacticAudiencePage` - same `tacticAudienceCards` lookup
 * (honors explicit `tactic.audienceSegmentIds`, falls back to the first 3
 * segments), same image-segment choice, same headline/body content fields.
 */
export function TacticAudiencePage({ proposal, section, actions, readOnly }: PageProps): ReactElement {
  const resolve = useBindings();
  const productId = section.productId;
  const { productName } = useContentLibrary();
  const label = productId ? (productName(productId) ?? productLabel(proposal, productId)) : section.title;
  const segments = tacticAudienceCards(proposal, productId);
  const imageSegment = segments.find((segment) => segment.imageUrl) ?? segments[0];
  const imageUrl = imageSegment?.imageUrl;

  return (
    <PageShell
      section={section}
      readOnly={readOnly}
      onTitleSave={(title) => actions.saveTitle(section, title)}
      className="mv3-tactic-audience-root"
      hideTitle
    >
      <Frame surface="light" proposal={proposal} section={section}>
        <div className="mv3-full-pane">
          <span className="mv3-eyebrow mv3-tactic-eyebrow-light">{`${label} / Audience`}</span>
          <div className="mv3-tactic-audience-cols">
            <div className="mv3-tactic-audience-photo">
              {imageUrl ? (
                <img src={imageUrl} alt={label} />
              ) : (
                <div className="audience-photo-placeholder" aria-hidden="true" />
              )}
              {!readOnly && imageSegment ? (
                <ImageSlot
                  url={imageSegment.imageUrl}
                  alt={imageSegment.name}
                  readOnly={readOnly}
                  onSave={(next) =>
                    actions.setAudience(
                      { segments: patchAudienceSegment(proposal.audience, imageSegment.id, { imageUrl: next }) },
                      `Changed ${imageSegment.name} image`,
                    )
                  }
                  className="mv3-tactic-audience-image-affordance"
                  hideImage
                  proposalId={proposal.proposalId}
                  defaultQuery={audienceImageQuery(proposal, imageSegment.segment)}
                  emptyState="neutral"
                />
              ) : null}
            </div>
            <div className="mv3-tactic-audience-right">
              <EditableText
                value={section.content.headline}
                field="headline"
                placeholder="Why this audience meets this channel"
                readOnly={readOnly}
                variant="headline"
                onSave={(headline) => actions.saveContent(section, { headline })}
              />
              <EditableText
                value={section.content.body}
                field="body"
                placeholder="Describe the audience fit"
                readOnly={readOnly}
                wordBudget={60}
                onSave={(body) => actions.saveContent(section, { body })}
              />
              {segments.length > 0 ? (
                <div className="mv3-tactic-audience-segments">
                  {segments.map((segment) => (
                    <TagPill tone="ghost" key={segment.id}>
                      {resolve(segment.name)}
                    </TagPill>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </Frame>
    </PageShell>
  );
}

/**
 * tactic_group (design.md V12): FULL light. Cards bind exactly like
 * momentum_v1's `TacticGroupPage` - role, `tacticGroupMetricLine`, and the
 * per-tactic blurb in `content.fields[productId]` - laid out as the shared
 * `SubCard` grid instead of momentum_v1's `NumberedCard`.
 */
export function TacticGroupPage({ proposal, section, actions, readOnly }: PageProps): ReactElement {
  const resolve = useBindings();
  const productIds = section.productIds ?? [];
  const { productName } = useContentLibrary();
  const fields = section.content.fields ?? {};
  const singleProductId = productIds.length === 1 ? productIds[0] : null;
  const displayTitle = tacticGroupDisplayTitle(proposal, productIds, section.title, productName);
  const setField = (key: string, value: string) => {
    actions.saveContent(section, { fields: { ...fields, [key]: value } });
  };

  return (
    <PageShell
      section={section}
      readOnly={readOnly}
      onTitleSave={(title) => actions.saveTitle(section, title)}
      className="mv3-tactic-group-root"
      hideTitle
    >
      <Frame surface="light" proposal={proposal} section={section}>
        <div className="mv3-full-pane">
          <HeadRow
            title={displayTitle}
            tagline={singleProductId ? undefined : 'How the supporting channels work together'}
          />
          <div className={`mv3-group-cards${singleProductId ? ' mv3-group-cards-single' : ''}`}>
            {productIds.map((productId, index) => {
              const label = productName(productId) ?? productLabel(proposal, productId);
              const line = channel(proposal, productId);
              const kind = deliveryKind(pricedLine(proposal, productId));
              const tactic = tacticFor(proposal, productId);
              const role = resolve(tactic?.role || defaultTacticRole(productId, kind));
              const metricLine = line ? tacticGroupMetricLine(tacticDeliveryMetrics(proposal, productId)) : null;
              return (
                <SubCard tone="light" className="mv3-group-card" key={productId}>
                  <div className="mv3-group-card-heading">
                    <NumberedLabel number={String(index + 1).padStart(2, '0')} label={label} />
                    <p className="mv3-group-card-role">{role}</p>
                  </div>
                  <div className="mv3-group-card-detail">
                    {metricLine ? <p className="mv3-group-card-metric">{metricLine}</p> : null}
                    <EditableText
                      value={fields[productId]}
                      field={`fields.${productId}` as SelectionField}
                      placeholder={`Add a note about ${label}`}
                      readOnly={readOnly}
                      wordBudget={25}
                      onSave={(next) => setField(productId, next)}
                    />
                  </div>
                </SubCard>
              );
            })}
          </div>
        </div>
      </Frame>
    </PageShell>
  );
}
